// What to show when a member has unrevealed badge copies: the level each one is
// shown at, the grouping by badge, the order and the words. Pure functions, no React.
//
// The tier is the badge's rarity: Paper, Rock and Bronze are tiles, Silver and Gold
// cards, the gems cinematics. First on the site lifts a copy one step; a repeat drops
// one step, except the 5th, 10th, 25th, 50th and 100th copy (at least a card).
import type { Tier, UnrevealedCopy } from "../types";
import { tierLabel, tierLine, tierRank, type TierLine } from "../collection/badgeFormat";
import { roundValue, valueOf } from "../value";

/** The three ways a badge is shown. */
export type ShownLevel = "tile" | "card" | "cinematic";

/**
 * Rising order: the tiles first, then the cards, then the cinematics, so the best
 * badge is the last and biggest moment.
 */
export const LEVEL_RANK: Record<ShownLevel, number> = { tile: 0, card: 1, cinematic: 2 };
const LEVELS: ShownLevel[] = ["tile", "card", "cinematic"];

/** The level of a first copy of each tier. */
export const TIER_LEVEL: Record<Tier, ShownLevel> = {
  PAPER: "tile",
  ROCK: "tile",
  BRONZE: "tile",
  SILVER: "card",
  GOLD: "card",
  RUBY: "cinematic",
  EMERALD: "cinematic",
  DIAMOND: "cinematic",
};

/** Repeat copies that keep their level and get at least a card. */
export const MILESTONE_REPEATS: ReadonlySet<number> = new Set([5, 10, 25, 50, 100]);

function clampRank(rank: number): number {
  return Math.max(0, Math.min(LEVELS.length - 1, rank));
}

/**
 * The level a copy is shown at, from its tier, whether it was the first on the
 * site, and which copy of the badge it is. The same rule as the backend's; used
 * when the server does not send a level of its own.
 */
export function levelFor(copy: { tier: Tier | string; firstEver?: boolean; repeatIndex?: number | null }): ShownLevel {
  let rank = LEVEL_RANK[TIER_LEVEL[String(copy.tier).toUpperCase() as Tier] ?? "tile"];
  if (copy.firstEver) rank = clampRank(rank + 1);
  const repeat = typeof copy.repeatIndex === "number" && Number.isFinite(copy.repeatIndex) ? Math.floor(copy.repeatIndex) : 1;
  if (repeat > 1) rank = MILESTONE_REPEATS.has(repeat) ? Math.max(rank, LEVEL_RANK.card) : clampRank(rank - 1);
  return LEVELS[rank];
}

/**
 * The level a copy is shown at: the server's, when it sends tile, card or
 * cinematic (history copies included). An older server sends "backfill" for a
 * history copy, and anything else it might send is unknown here: those are worked
 * out with levelFor.
 */
export function revealLevelOf(copy: Pick<UnrevealedCopy, "revealLevel" | "tier" | "firstEver" | "repeatIndex">): ShownLevel {
  const level: string = copy.revealLevel;
  if (level === "tile" || level === "card" || level === "cinematic") return level;
  return levelFor(copy);
}

export function isBigReveal(copy: Pick<UnrevealedCopy, "revealLevel" | "tier" | "firstEver" | "repeatIndex">): boolean {
  return revealLevelOf(copy) !== "tile";
}

function timeOf(iso: string | undefined): number {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : 0;
}

/**
 * The unrevealed copies of one badge, shown as one reveal with their count: the
 * two-day streak earned 71 times is one reveal showing x71 and what the 71 are
 * worth together, and collecting it collects all 71.
 */
export interface RevealGroup {
  code: string;
  /** The highest level among the copies. */
  level: ShownLevel;
  /** Oldest first. */
  copies: UnrevealedCopy[];
  copyIds: string[];
  /** The newest copy: its art and name are what a tile shows. */
  latest: UnrevealedCopy;
  /** Copies held of this code (the "x3" chip), 1 for a first copy. */
  count: number;
  /** One of the copies was the first on the site. */
  firstEver: boolean;
  /**
   * What the copies are worth together, shown as "+N". Each copy counts at its own
   * value (a repeat is worth 4.5% less than the copy before it). null when the
   * badge is worth nothing.
   */
  value: number | null;
  /**
   * What a card or a cinematic shows: the newest copy at the group's level. When
   * the group folds more than one copy it carries the group's count (as its repeat
   * number, so the reveal says x71), the group's value, and first on the site when
   * any copy was.
   */
  display: UnrevealedCopy;
}

/** Tile copies of one badge code, shown as one tile. */
export type TileGroup = RevealGroup;

export interface RevealPlan {
  /** Every group, worst first. */
  groups: RevealGroup[];
  /** The next card or cinematic, the worst one left, or null. */
  current: RevealGroup | null;
  /** Cards and cinematics queued behind `current`. */
  waiting: number;
  /** Tile groups, worst first. They show at once, before the cards and cinematics. */
  tiles: RevealGroup[];
}

function groupOf(code: string, list: Array<{ copy: UnrevealedCopy; index: number }>): RevealGroup & { first: number; since: number } {
  const ordered = list
    .slice()
    .sort((a, b) => timeOf(a.copy.dateEarned) - timeOf(b.copy.dateEarned) || (a.copy.repeatIndex || 0) - (b.copy.repeatIndex || 0) || a.index - b.index);
  const copies = ordered.map((x) => x.copy);
  const levels = copies.map(revealLevelOf);
  const top = Math.max(...levels.map((l) => LEVEL_RANK[l]));
  const level = (Object.keys(LEVEL_RANK) as ShownLevel[]).find((l) => LEVEL_RANK[l] === top) ?? "tile";
  const latest = copies[copies.length - 1];
  // the newest copy that sets the level
  let base = latest;
  for (let i = copies.length - 1; i >= 0; i--) {
    if (levels[i] === level) {
      base = copies[i];
      break;
    }
  }
  const count = Math.max(1, copies.length, ...copies.map((c) => c.count || 0), ...copies.map((c) => c.repeatIndex || 0));
  const total = roundValue(copies.reduce((sum, c) => sum + valueOf(c), 0));
  const firstEver = copies.some((c) => c.firstEver);
  const display = copies.length > 1 ? { ...base, count, repeatIndex: count, value: total, firstEver } : base;
  return {
    code,
    level,
    copies,
    copyIds: copies.map((c) => c.copyId),
    latest,
    count,
    firstEver,
    value: total > 0 ? total : null,
    display,
    first: Math.min(...list.map((x) => x.index)),
    since: timeOf(copies[0].dateEarned),
  };
}

/**
 * Worst first: by level (tile, card, cinematic), then tier (Paper up to Diamond),
 * with a first-on-the-site copy after the others of its tier, then points. Older
 * first, then the server's order, break what is left. Fresh and history copies go
 * in one queue.
 */
function compareRising(a: ReturnType<typeof groupOf>, b: ReturnType<typeof groupOf>): number {
  return (
    LEVEL_RANK[a.level] - LEVEL_RANK[b.level] ||
    tierRank(a.display.tier) - tierRank(b.display.tier) ||
    Number(a.firstEver) - Number(b.firstEver) ||
    (a.display.points || 0) - (b.display.points || 0) ||
    a.since - b.since ||
    a.first - b.first
  );
}

/** Fold the copies into one group per badge code, worst first. */
export function groupReveals(items: readonly UnrevealedCopy[]): RevealGroup[] {
  const byCode = new Map<string, Array<{ copy: UnrevealedCopy; index: number }>>();
  items.forEach((copy, index) => {
    const list = byCode.get(copy.code);
    if (list) list.push({ copy, index });
    else byCode.set(copy.code, [{ copy, index }]);
  });
  return Array.from(byCode.entries())
    .map(([code, list]) => groupOf(code, list))
    .sort(compareRising)
    .map(({ first: _first, since: _since, ...group }) => group);
}

/**
 * Route the unrevealed copies. The tiles show at once; the cards and then the
 * cinematics follow one at a time, worst first. `handled` holds copy ids the
 * member already dealt with in this session, so they vanish before the server
 * confirms.
 */
export function planReveals(items: readonly UnrevealedCopy[], handled?: ReadonlySet<string>): RevealPlan {
  const groups = groupReveals(handled && handled.size ? items.filter((i) => !handled.has(i.copyId)) : items);
  const big = groups.filter((g) => g.level !== "tile");
  return {
    groups,
    current: big[0] ?? null,
    waiting: Math.max(0, big.length - 1),
    tiles: groups.filter((g) => g.level === "tile"),
  };
}

// ---------- words ----------

export interface RaritySnapshot {
  holders: number;
  members: number;
}

/** How many held it when the copy was earned, else how many hold the badge now. */
export function rarityOf(copy: Pick<UnrevealedCopy, "rarityAtEarn" | "rarity">): RaritySnapshot {
  if (copy.rarityAtEarn) return { holders: copy.rarityAtEarn.holders ?? 0, members: copy.rarityAtEarn.members ?? 0 };
  return { holders: copy.rarity?.holders ?? 0, members: copy.rarity?.members ?? 0 };
}

/**
 * The line under a revealed badge, the tier and the holders fact:
 *   "Ruby. 2 of 41 members have it."
 *   "Gold. Nobody else has it."            (a unique badge, or the only holder)
 *   "First on the site. Silver. Nobody else has it."
 */
export function revealLine(copy: Pick<UnrevealedCopy, "tier" | "rarityAtEarn" | "rarity" | "unique" | "firstEver">): TierLine {
  const r = rarityOf(copy);
  return tierLine({ tier: copy.tier, holders: r.holders, members: r.members, unique: copy.unique, firstEver: copy.firstEver });
}

/**
 * Badge points as the tiles show them: whole numbers stay whole, others keep up to
 * two decimals, as the collection shows them (7.25 stays 7.25, not 7.3).
 */
export function formatPoints(points: number): string {
  if (!Number.isFinite(points)) return "0";
  return String(roundValue(points));
}

/** "+1 point", "+7.25 points" */
export function pointsLabel(points: number): string {
  const text = formatPoints(points);
  return `+${text} ${text === "1" ? "point" : "points"}`;
}

/** "x3" for a repeat copy, null for a first copy. */
export function repeatLabel(count: number | undefined): string | null {
  return count && count > 1 ? `x${Math.floor(count)}` : null;
}

/** "Skip the other Silver (4)": the tier as the art names it, and how many of it are queued. */
export function skipTierLabel(tier: string, n: number): string {
  return `Skip the other ${tierLabel(tier)} (${n})`;
}

/** Where a tile and "See collection" send the member. */
export function collectionPath(code?: string): string {
  return code ? `/badges?badge=${encodeURIComponent(code)}` : "/badges";
}
